import { FC } from 'react';
import Image from 'next/image';
import Link from 'next/link';

import AppQrCode from './AppQrCode';
import { useDeviceOs } from '@hooks/useDeviceOs';
import { APP_STORE_URL, GOOGLE_PLAY_URL } from '@lib/constants';

// Both badge assets share a ~3.37:1 aspect ratio.
const STORE_BADGE_HEIGHT = 48;
const STORE_BADGE_WIDTH = 162;

const AppStoreBadge: FC = () => (
  <Link href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
    <Image
      src="/images/app-store-badge.png"
      alt="Download on the App Store"
      height={STORE_BADGE_HEIGHT}
      width={STORE_BADGE_WIDTH}
    />
  </Link>
);

const GooglePlayBadge: FC = () => (
  <Link href={GOOGLE_PLAY_URL} target="_blank" rel="noopener noreferrer">
    <Image
      src="/images/google-play-badge.svg"
      alt="Get it on Google Play"
      height={STORE_BADGE_HEIGHT}
      width={STORE_BADGE_WIDTH}
    />
  </Link>
);

/**
 * Device-appropriate app download block: phones get their own store's
 * badge, desktop gets both badges plus a QR code that resolves through
 * the device-aware /app redirect.
 */
const AppDownloadSection: FC = () => {
  const { isIos, isAndroid, isMobile } = useDeviceOs();

  return (
    <section className="mt-10 rounded-2xl border border-neutral-200 bg-white px-6 py-8">
      <h2 className="text-center font-sans text-xl font-extrabold text-neutral-900">
        Want the best experience?
      </h2>
      <p className="mt-2 text-center font-open-sans text-sm text-neutral-600">
        Download the app. It&apos;s how winners play.
      </p>

      <div className="mt-6 flex flex-col items-center gap-6">
        {isIos && <AppStoreBadge />}
        {isAndroid && <GooglePlayBadge />}
        {!isMobile && (
          <>
            <AppQrCode />
            <div className="flex flex-wrap items-center justify-center gap-4">
              <AppStoreBadge />
              <GooglePlayBadge />
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default AppDownloadSection;
